import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DollarSign, Receipt, History, ArrowLeft, User, Trash2 } from 'lucide-react';
import api from '@/api/api';
import PaymentBreakdown from '@/components/PaymentBreakdown';
import { generateFeePaymentReceipt } from '@/utils/feePaymentReceiptGenerator';

const StudentFeePayment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const studentIdParam = searchParams.get('student_id');

  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(studentIdParam || '');
  const [feeSummary, setFeeSummary] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);

  // Current user role (only Admin can delete payments)
  const userRole = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role || ''; } catch { return ''; }
  })();
  const canDeletePayment = userRole === 'Admin' || userRole === 'SuperAdmin';

  const [paymentData, setPaymentData] = useState({
    amount_paid: 0,
    payment_modes: [],
    payment_for: 'Course Fee',
    remarks: ''
  });

  useEffect(() => {
    fetchStudents();
    loadCollegeInfo();
    if (studentIdParam) {
      setSelectedStudentId(studentIdParam);
      fetchFeeSummary(studentIdParam);
      fetchPaymentHistory(studentIdParam);
    }
  }, [studentIdParam]);

  const loadCollegeInfo = async () => {
    try {
      const response = await api.get('/tenants/my-college/settings');
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await api.get(`/students/dropdown`);
      const studentsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const approvedStudents = studentsData.filter(s => s.is_approved);
      setStudents(approvedStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchFeeSummary = async (studentId) => {
    try {
      setLoading(true);
      const response = await api.get(`/fee-payments/student/${studentId}/summary`);
      setFeeSummary(response.data);
    } catch (error) {
      console.error('Error fetching fee summary:', error);
      alert('Error fetching fee summary');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async (studentId) => {
    try {
      const response = await api.get(`/fee-payments/student/${studentId}`);
      setPaymentHistory(response.data);
    } catch (error) {
      console.error('Error fetching payment history:', error);
    }
  };

  const deletePayment = async (payment) => {
    const receiptLabel = payment.receipt_no || payment.payment_id;
    const amount = payment.amount_paid;
    const confirmMsg =
      `Delete this payment?\n\n` +
      `Receipt No: ${receiptLabel}\n` +
      `Amount: ₹${Number(amount || 0).toLocaleString('en-IN')}\n\n` +
      `THIS WILL ALSO REMOVE THE ENTRY FROM:\n` +
      `• Cash Book (if paid by Cash)\n` +
      `• Bank Book + Bank balance will be reversed (if non-Cash)\n` +
      `• Income Reports\n\n` +
      `This cannot be undone. The student's outstanding balance will be recalculated.`;
    if (!window.confirm(confirmMsg)) return;

    const paymentId = payment.payment_id || payment.id;
    if (!paymentId) {
      alert('Cannot delete: payment_id is missing');
      return;
    }

    setDeletingPaymentId(paymentId);
    try {
      const res = await api.delete(`/fee-payments/${paymentId}`);
      const c = res.data?.cascade || {};
      alert(
        `Payment ${receiptLabel} deleted successfully.\n\n` +
        `Cascade cleanup:\n` +
        `• Incomes removed: ${c.incomes_deleted || 0}\n` +
        `• Cash Book entries removed: ${c.cash_register_deleted || 0}\n` +
        `• Bank Book entries removed: ${c.bank_transactions_deleted || 0}\n` +
        `• Bank balance reversed: ${c.banks_balance_reversed || 0}\n` +
        `• Fee installments reset: ${c.fee_installments_reset || 0}\n` +
        `• Student outstanding updated: ${c.student_balance_updated ? 'Yes' : 'No'}`
      );
      // Refresh both summary + history
      if (selectedStudentId) {
        await fetchFeeSummary(selectedStudentId);
        await fetchPaymentHistory(selectedStudentId);
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Failed to delete payment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const handleStudentChange = (studentId) => {
    setSelectedStudentId(studentId);
    if (studentId) {
      fetchFeeSummary(studentId);
      fetchPaymentHistory(studentId);
      setShowPaymentForm(false);
    } else {
      setFeeSummary(null);
      setPaymentHistory([]);
      setShowPaymentForm(false);
    }
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    // Validate payment modes
    if (!paymentData.payment_modes || paymentData.payment_modes.length === 0) {
      alert('Please add at least one payment mode');
      return;
    }

    const totalPaid = paymentData.payment_modes.reduce((sum, mode) => sum + parseFloat(mode.amount || 0), 0);
    if (Math.abs(totalPaid - paymentData.amount_paid) > 0.01) {
      alert(`Payment breakdown total (₹${totalPaid.toLocaleString('en-IN')}) must equal payment amount (₹${paymentData.amount_paid.toLocaleString('en-IN')})`);
      return;
    }

    if (paymentData.amount_paid <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    if (paymentData.amount_paid > feeSummary.outstanding_balance) {
      const confirm = window.confirm(
        `Payment amount (₹${paymentData.amount_paid.toLocaleString('en-IN')}) exceeds outstanding balance (₹${feeSummary.outstanding_balance.toLocaleString('en-IN')}). Do you want to continue?`
      );
      if (!confirm) return;
    }

    setLoading(true);
    try {
      const student = students.find(s => s.id === selectedStudentId);
      
      const payment = {
        student_id: selectedStudentId,
        student_name: student.full_name,
        admission_no: student.admission_number,
        course_name: student.course_name,
        session: student.session,
        amount_paid: parseFloat(paymentData.amount_paid),
        payment_modes: paymentData.payment_modes,
        total_fee: feeSummary.total_fee,
        previously_paid: feeSummary.total_paid,
        total_paid_after: feeSummary.total_paid + parseFloat(paymentData.amount_paid),
        outstanding_balance: feeSummary.outstanding_balance - parseFloat(paymentData.amount_paid),
        payment_for: paymentData.payment_for,
        remarks: paymentData.remarks,
        collected_by: 'Admin' // In production, use logged-in user
      };

      const response = await api.post(`/fee-payments/`, payment);
      
      alert('Payment recorded successfully!');
      
      // Refresh data
      fetchFeeSummary(selectedStudentId);
      fetchPaymentHistory(selectedStudentId);
      
      // Reset form
      setPaymentData({
        amount_paid: 0,
        payment_modes: [],
        payment_for: 'Course Fee',
        remarks: ''
      });
      setShowPaymentForm(false);

      // Generate receipt with correct tenant info
      const receiptData = {
        ...payment,
        receipt_no: response.data.receipt_no,
        payment_id: response.data.payment_id,
        payment_date: new Date().toISOString().split('T')[0]
      };
      await generateFeePaymentReceipt(receiptData, collegeInfo);

    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Error recording payment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pay Fee - Running Balance</h1>
            <p className="text-sm text-gray-600">Record student fee payments with partial payment support</p>
          </div>
        </div>
      </div>

      {/* Student Selection */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <User className="inline mr-2" size={18} />
          Select Student *
        </label>
        <select
          value={selectedStudentId}
          onChange={(e) => handleStudentChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          required
        >
          <option value="">Choose a student...</option>
          {students.map(student => (
            <option key={student.id} value={student.id}>
              {student.admission_number} - {student.full_name} ({student.course_name})
            </option>
          ))}
        </select>
      </div>

      {/* Fee Summary */}
      {feeSummary && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign size={24} className="text-blue-600" />
            Fee Summary
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Total Fee</p>
              <p className="text-2xl font-bold text-blue-600">₹{feeSummary.total_fee.toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Total Paid</p>
              <p className="text-2xl font-bold text-green-600">₹{feeSummary.total_paid.toLocaleString('en-IN')}</p>
              <p className="text-xs text-gray-500">
                Initial: ₹{feeSummary.initial_paid.toLocaleString('en-IN')} + 
                Payments: ₹{feeSummary.fee_payments_total.toLocaleString('en-IN')}
              </p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Outstanding Balance</p>
              <p className="text-2xl font-bold text-orange-600">₹{feeSummary.outstanding_balance.toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Payments Made</p>
              <p className="text-2xl font-bold text-purple-600">{feeSummary.payment_count}</p>
            </div>
          </div>

          {!showPaymentForm && feeSummary.outstanding_balance > 0 && (
            <button
              onClick={() => setShowPaymentForm(true)}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <DollarSign size={18} />
              Make Payment
            </button>
          )}

          {feeSummary.outstanding_balance <= 0 && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
              ✅ All fees paid! No outstanding balance.
            </div>
          )}
        </div>
      )}

      {/* Payment Form */}
      {showPaymentForm && feeSummary && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Record Payment (Partial Payment Allowed)</h2>
          
          <form onSubmit={handleSubmitPayment} className="space-y-6">
            {/* Payment Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Amount *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
                <input
                  type="number"
                  value={paymentData.amount_paid}
                  onChange={(e) => setPaymentData({...paymentData, amount_paid: parseFloat(e.target.value) || 0})}
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                  required
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Outstanding: ₹{feeSummary.outstanding_balance.toLocaleString('en-IN')} | You can pay any amount (partial payment allowed)
              </p>
            </div>

            {/* Payment Breakdown */}
            {paymentData.amount_paid > 0 && (
              <PaymentBreakdown
                totalAmount={paymentData.amount_paid}
                paymentModes={paymentData.payment_modes}
                onChange={(modes) => setPaymentData({...paymentData, payment_modes: modes})}
                label="Payment Breakdown *"
              />
            )}

            {/* Payment For */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment For
              </label>
              <select
                value={paymentData.payment_for}
                onChange={(e) => setPaymentData({...paymentData, payment_for: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Course Fee">Course Fee</option>
                <option value="Exam Fee">Exam Fee</option>
                <option value="Library Fee">Library Fee</option>
                <option value="Lab Fee">Lab Fee</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks (Optional)
              </label>
              <textarea
                value={paymentData.remarks}
                onChange={(e) => setPaymentData({...paymentData, remarks: e.target.value})}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Add any notes about this payment..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
              >
                <Receipt size={18} />
                {loading ? 'Processing...' : 'Record Payment & Generate Receipt'}
              </button>
              <button
                type="button"
                onClick={() => setShowPaymentForm(false)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Payment History */}
      {paymentHistory.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <History size={24} className="text-gray-600" />
            Payment History
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount Paid</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Modes</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance After</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment For</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paymentHistory.map((payment, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{payment.payment_date}</td>
                    <td className="px-4 py-3 text-sm font-medium text-blue-600">{payment.receipt_no}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-green-600">₹{payment.amount_paid.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {payment.payment_modes.map((pm, idx) => (
                          <div key={idx} className="inline-flex flex-col items-start px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800">
                            <span className="font-medium">{pm.mode}: ₹{parseFloat(pm.amount).toLocaleString('en-IN')}</span>
                            {pm.transaction_number && (
                              <span className="text-xs text-blue-600">Ref: {pm.transaction_number}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-orange-600">₹{payment.outstanding_balance.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{payment.payment_for}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={async () => await generateFeePaymentReceipt(payment, collegeInfo)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium inline-flex items-center"
                          data-testid={`receipt-btn-${payment.receipt_no || payment.payment_id}`}
                        >
                          <Receipt size={16} className="inline mr-1" />
                          Receipt
                        </button>
                        {canDeletePayment && (
                          <button
                            onClick={() => deletePayment(payment)}
                            disabled={deletingPaymentId === (payment.payment_id || payment.id)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium inline-flex items-center disabled:opacity-50"
                            title="Delete this payment"
                            data-testid={`delete-payment-btn-${payment.receipt_no || payment.payment_id}`}
                          >
                            <Trash2 size={16} className="inline mr-1" />
                            {deletingPaymentId === (payment.payment_id || payment.id) ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!selectedStudentId && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-center">
          Please select a student to view fee details and make payments
        </div>
      )}
    </div>
  );
};

export default StudentFeePayment;
